<template>
    <div class="row h-100 d-flex flex-column flex-nowrap">
        <!-- Subscription pause/cancel -->
        <subscription-pause-cancel-dialog
            v-if="subscription && subscription.Id"
            v-model="showSubscriptionPauseCancelDialog"
            :subscription-id="subscription.Id"
            :allow-pausing="!subscriptionPausedOrWillPause"
            :allow-canceling="cancelPauseDialogAllowCancel"
            :account-disable-mode="accountDisableMode"
            :store="storeUrl"
            :card-token="pauseCardToken"
            @canceled="onSubscriptionCancel"
            @paused="onSubscriptionPaused"
        />

        <!-- Subscription resume -->
        <subscription-resume-dialog
            v-if="subscription && subscription.Id"
            v-model="showSubscriptionResumeDialog"
            :subscription-id="subscription.Id"
            :store="storeUrl"
            @resumed="onSubscriptionResumed"
        />

        <!-- Credit card update modal -->
        <b-modal
            v-model="showUpdateCardDialog"
            size="lg"
            title="Update billing"
            modal-class="vue-need-bootstrap"
            lazy
            centered
            hide-footer
            no-close-on-backdrop
            class="mt-3"
        >
            <credit-card-input :stripe-public-key="stripePublicKey" :store="storeUrl" @card-updated="onCardSubmit" />
        </b-modal>

        <!-- Credit card for pause modal -->
        <b-modal
            v-model="showCardAskDialog"
            size="lg"
            title="Enter card"
            modal-class="vue-need-bootstrap"
            lazy
            centered
            hide-footer
            no-close-on-backdrop
            class="mt-3"
        >
            <credit-card-input
                :no-save="true"
                :stripe-public-key="stripePublicKey"
                :store="storeUrl"
                @token="onCardForPauseSubmit"
            />
        </b-modal>

        <!-- Store selection dropdown -->
        <store-selector :stores="userStores" @input="setStoreUrl" />

        <!-- Plan information -->

        <customily-plan-card
            v-if="subscription"
            :loading="loadingSubscriptions"
            :store="storeUrl"
            :subscription="subscription"
        />

        <div v-if="!isShopifyNewPlan">
            <!-- Credit card -->
            <div class="mt-3">
                <div class="card white-card shadow-sm">
                    <div class="card-body">
                        <h4 class="card-title mb-3">Billing Details</h4>
                        <div :class="{ busy: loadingPaymentMethod }">
                            <div v-if="loadingPaymentMethod" class="text-center">
                                <b-spinner variant="primary" class="my-2" />
                            </div>
                            <div
                                v-else
                                class="row small-card-content"
                                :class="{ 'has-card': paymentMethod && subscription && customer }"
                            >
                                <div class="col-10 row">
                                    <!-- Card information -->
                                    <div class="col-6">
                                        <div
                                            v-if="paymentMethod && subscription && customer"
                                            class="align-items-center d-flex"
                                        >
                                            <!-- Card icon -->
                                            <card-icon class="d-inline" :card="paymentMethod.Brand" />

                                            <div>
                                                <h5 class="d-inline ml-2">Card ending in {{ paymentMethod.Last4 }}</h5>
                                                <small class="card-expiration card-expiration ml-1">
                                                    exp. {{ paymentMethod.ExpMonth.toString().padStart(2, '0') }} /{{
                                                        paymentMethod.ExpYear
                                                    }}
                                                </small>
                                            </div>
                                        </div>
                                        <div
                                            v-if="!paymentMethod && subscription && customer"
                                            class="align-items-center d-flex"
                                        >
                                            No credit card added.
                                        </div>
                                        <div v-if="!subscription || !customer" class="align-items-center d-flex">
                                            You don't have any active subscriptions. Please contact us.
                                        </div>

                                        <div v-if="customer && customer.Email" class="mt-3">
                                            Billing contact:
                                            <strong>{{ customer.Email }}</strong>
                                        </div>
                                    </div>

                                    <!-- Billing information -->
                                    <div v-if="paymentMethod" class="col-6">
                                        <ul>
                                            <li v-if="paymentMethod.BillingDetails.Name">
                                                {{ paymentMethod.BillingDetails.Name }}
                                            </li>
                                            <li v-if="paymentMethod.BillingDetails.Address1">
                                                {{ paymentMethod.BillingDetails.Address1 }}
                                            </li>
                                            <li
                                                v-if="
                                                    paymentMethod.BillingDetails.Country &&
                                                    paymentMethod.BillingDetails.City
                                                "
                                            >
                                                {{ paymentMethod.BillingDetails.City }},
                                                {{ paymentMethod.BillingDetails.Country }}
                                                <span v-if="paymentMethod.BillingDetails.State">
                                                    ,{{ paymentMethod.BillingDetails.State }}
                                                </span>
                                                <span v-if="paymentMethod.BillingDetails.PostalCode">
                                                    , {{ paymentMethod.BillingDetails.PostalCode }}
                                                </span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div
                                    v-if="subscription && customer"
                                    class="col d-flex align-items-end justify-content-end"
                                >
                                    <button class="btn btn-primary" @click="editCreditCard()">Edit billing</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Invoices -->
            <div class="mt-3 invoice-card">
                <div class="card white-card shadow-sm h-100">
                    <div class="card-body d-flex flex-column">
                        <h4 class="card-title mb-3">Billing History</h4>

                        <b-table
                            class="invoice-table flex-grow-1"
                            sticky-header
                            hover
                            fixed
                            show-empty
                            :fields="invoiceTableFields"
                            :items="invoiceItems"
                            :busy="loadingInvoices"
                            empty-text="No invoices"
                        >
                            <!-- Empty table indicator -->
                            <template #empty="scope">
                                <div class="text-center mt-3">
                                    <h5>{{ scope.emptyText }}</h5>
                                </div>
                            </template>

                            <!-- Loader -->
                            <template #table-busy>
                                <div class="align-items-center d-flex justify-content-center w-100 mt-3">
                                    <b-spinner variant="primary" class="my-2" />
                                </div>
                            </template>

                            <!-- Date -->
                            <template #cell(date)="data">
                                <div>{{ data.value }}</div>
                            </template>

                            <!-- Description -->
                            <template #cell(description)>
                                <div>Customily Service</div>
                            </template>

                            <!-- Amount -->
                            <template #cell(amount)="data">
                                <div>{{ data.value }}</div>
                            </template>

                            <!-- Status -->
                            <template #cell(status)="data">
                                <div
                                    class="text-capitalize"
                                    :class="{ paid: data.value === 'paid', 'primary-text': data.value === 'paid' }"
                                >
                                    {{ data.value }}
                                </div>
                            </template>

                            <!-- View invoice -->
                            <template #cell(invoice)="data">
                                <a :href="data.value" target="_blank" rel="noopener noreferrer">
                                    <i class="fa fa-eye invoice-icon" />
                                </a>
                            </template>
                        </b-table>

                        <!-- Pagination buttons -->
                        <div class="d-flex justify-content-center">
                            <div class="btn-group">
                                <button
                                    class="btn btn-secondary btn-white shadow-none"
                                    type="button"
                                    :class="{ disabled: loadingInvoices || disablePrevBtn }"
                                    @click="goPrevInvoicesPage()"
                                >
                                    ‹
                                </button>
                                <button
                                    class="btn btn-secondary btn-white shadow-none"
                                    type="button"
                                    :class="{ disabled: loadingInvoices || disableNextBtn }"
                                    @click="goNextInvoicesPage()"
                                >
                                    ›
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pause subscription -->
            <div class="mt-3">
                <div class="card white-card shadow-sm">
                    <div class="card-body">
                        <h4 class="card-title mb-3">Pause {{ actionEntityStringCapitalized }} Subscription</h4>
                        <div :class="{ busy: loadingSubscriptions }">
                            <div v-if="loadingSubscriptions" class="text-center">
                                <b-spinner variant="primary" class="my-2" />
                            </div>

                            <!-- Active subscription -->
                            <div
                                v-if="
                                    !loadingSubscriptions &&
                                    subscription &&
                                    hasActiveSubscription &&
                                    !subscription.WillPauseAt &&
                                    !subscription.WillCancelAt
                                "
                                class="row small-card-content"
                            >
                                <div class="col-8">
                                    You can pause your subscription and save all your template and options information
                                    to use it on your products later on <strong>for $5 a month</strong>. Your products
                                    will no longer have options and previews live, but all your work will be saved so
                                    you can come back whenever you are ready and activate them.

                                    <br />
                                    <br />

                                    <!-- If subscription is past due, it will be paused immediately  -->
                                    <template v-if="subscription.Status === 'past_due'">
                                        If you pause your subscription,
                                        <strong>Customily will immediately stop working on your store.</strong>
                                    </template>

                                    <!-- Otherwise subscription is paused at the end of the billing cycle -->
                                    <template v-else>
                                        If you pause your subscription, Customily will work on your store until the end
                                        of your billing period:
                                        <strong>{{ formatDateString(subscription.CurrentPeriodEnd) }}</strong>
                                    </template>
                                </div>
                                <div class="col d-flex align-items-end justify-content-end">
                                    <button
                                        class="btn btn-secondary"
                                        @click="startSubscriptionPauseFlow(subscription.Status === 'past_due')"
                                    >
                                        Pause
                                        {{ actionEntityString }} for $5/month
                                    </button>
                                </div>
                            </div>

                            <!-- Paused subscription -->
                            <div
                                v-if="
                                    subscription &&
                                    (subscription.Status === 'paused' || subscription.Status === 'paused_past_due')
                                "
                                class="row"
                            >
                                <div class="col-8">
                                    {{ actionEntityStringCapitalized }} is paused. To continue using our services please
                                    resume it.
                                </div>
                                <div class="col d-flex align-items-end justify-content-end">
                                    <button class="btn btn-primary" @click="startSubscriptionResumeFlow()">
                                        Resume {{ actionEntityStringCapitalized }}
                                    </button>
                                </div>
                            </div>

                            <!-- Active subscription that will be paused -->
                            <div
                                v-if="
                                    subscription &&
                                    hasActiveSubscription &&
                                    subscription.WillPauseAt &&
                                    !subscription.WillCancelAt
                                "
                                class="row"
                            >
                                <div class="col-8">
                                    Your {{ actionEntityString }} will be paused on
                                    <b>{{ formatDateString(subscription.WillPauseAt) }}</b>
                                    . You can avoid it by resuming your {{ actionEntityString }}.
                                </div>
                                <div class="col-8 d-flex align-items-end justify-content-end">
                                    <button class="btn btn-primary" @click="startSubscriptionResumeFlow()">
                                        Resume {{ actionEntityStringCapitalized }}
                                    </button>
                                </div>
                            </div>

                            <!-- Subscription canceled by the user but user not yet canceled -->
                            <div v-if="subscription && hasActiveSubscription && subscription.WillCancelAt" class="row">
                                <div class="col-8">
                                    Your {{ actionEntityString }} will be canceled on
                                    <b>{{ formatDateString(subscription.WillCancelAt) }}</b>
                                    . You can avoid it by resuming your {{ actionEntityString }}.
                                </div>
                                <div class="col d-flex align-items-end justify-content-end">
                                    <button class="btn btn-primary" @click="startSubscriptionResumeFlow()">
                                        Resume {{ actionEntityStringCapitalized }}
                                    </button>
                                </div>
                            </div>

                            <!-- Store canceled by the user -->
                            <div v-if="subscription && subscription.Status === 'canceled'">
                                Your {{ actionEntityString }} has been canceled. Please contact us if you wish to resume
                                using our services.
                            </div>

                            <!-- Subscription canceled because of payment failure or non existent -->
                            <div
                                v-if="
                                    !subscription ||
                                    (subscription &&
                                        (subscription.Status === 'unpaid' ||
                                            subscription.Status === 'incomplete' ||
                                            subscription.Status === 'incomplete_expired'))
                                "
                            >
                                You don't have any active subscriptions. Please contact us.
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Cancel subscription -->
            <div class="mt-3 pb-3">
                <div class="card white-card shadow-sm">
                    <div class="card-body">
                        <h4 class="card-title mb-3">Cancel {{ actionEntityStringCapitalized }} Subscription</h4>
                        <div :class="{ busy: loadingSubscriptions }">
                            <div v-if="loadingSubscriptions" class="text-center">
                                <b-spinner variant="primary" class="my-2" />
                            </div>
                            <template v-else>
                                <!-- Active subscription -->
                                <div
                                    v-if="subscription && hasActiveSubscription && !subscription.WillCancelAt"
                                    class="row small-card-content"
                                >
                                    <div class="col-8">
                                        <b v-if="!accountDisableMode">
                                            If you cancel, all your templates, libraries, and personalization
                                            information will be disconnected from your {{ actionEntityString }}.</b
                                        >

                                        <b v-if="accountDisableMode">
                                            If you cancel, all your templates, libraries, and personalization
                                            information will be lost.</b
                                        >

                                        If you wish to save this information to use it in your {{ actionEntityString }}
                                        again in the future you can
                                        <b>pause your {{ actionEntityString }} instead.</b>

                                        <br />
                                        <br />

                                        <!-- If subscription is past due, it will be canceled immediately  -->
                                        <template v-if="subscription.Status === 'past_due'">
                                            If you cancel your subscription,
                                            <b>Customily will immediately stop working on your store.</b>
                                        </template>

                                        <!-- Otherwise subscription is canceled at the end of the billing cycle -->
                                        <template v-else>
                                            If you cancel your subscription, Customily will work on your store until the
                                            end of your billing period:
                                            <b>{{ formatDateString(subscription.CurrentPeriodEnd) }}</b>
                                        </template>
                                    </div>
                                    <div class="col d-flex align-items-end justify-content-end">
                                        <button class="btn btn-secondary" @click="startSubscriptionCancelFlow()">
                                            Cancel {{ actionEntityStringCapitalized }}
                                        </button>
                                    </div>
                                </div>

                                <!-- Paused subscription -->
                                <div
                                    v-if="
                                        subscription &&
                                        (subscription.Status === 'paused' || subscription.Status === 'paused_past_due')
                                    "
                                    class="row"
                                >
                                    <div class="col-8">
                                        {{ actionEntityStringCapitalized }} is paused. To continue using our services
                                        please resume it. You can also permanently cancel your {{ actionEntityString }}.
                                    </div>
                                    <div class="col d-flex align-items-end justify-content-end">
                                        <button class="btn btn-secondary" @click="startSubscriptionCancelFlow()">
                                            Cancel {{ actionEntityStringCapitalized }}
                                        </button>
                                    </div>
                                </div>

                                <!-- Subscription canceled by the user but user not yet canceled -->
                                <div
                                    v-if="subscription && hasActiveSubscription && subscription.WillCancelAt"
                                    class="row"
                                >
                                    <div class="col-8">
                                        Your {{ actionEntityString }} will be canceled on
                                        <b>{{ formatDateString(subscription.WillCancelAt) }}</b>
                                        . You can avoid it by resuming your {{ actionEntityString }}.
                                    </div>
                                    <div class="col d-flex align-items-end justify-content-end">
                                        <button class="btn btn-primary" @click="startSubscriptionResumeFlow()">
                                            Resume {{ actionEntityStringCapitalized }}
                                        </button>
                                    </div>
                                </div>

                                <!-- Subscription canceled by the user -->
                                <div v-if="subscription && subscription.Status === 'canceled'">
                                    Your {{ actionEntityString }} has been canceled. Please contact us if you wish to
                                    resume using our services.
                                </div>

                                <!-- Subscription canceled because of payment failure or non existent -->
                                <div
                                    v-if="
                                        !subscription ||
                                        (subscription &&
                                            (subscription.Status === 'unpaid' ||
                                                subscription.Status === 'incomplete' ||
                                                subscription.Status === 'incomplete_expired'))
                                    "
                                >
                                    You don't have any active subscriptions. Please contact us.
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="mt-3">
            <!-- Shopify only-->
            <shopify-billing />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Ref, computed, onBeforeMount, ref, watch } from 'vue';
import dateformat from 'dateformat';
import store from '@/store';
import { getCustomer, getInvoices, getPaymentMethod, getSubscriptions } from '@/api/billing';
import type { AxiosError } from 'axios';
import type { Customer, Invoice, PaymentMethod, Subscription, SubscriptionStatus } from '@/api/typings/billing';
import CardIcon from '@/views/Account/components/CardIcon.vue';
import CreditCardInput from '@/views/Account/components/CreditCardInput.vue';
import SubscriptionPauseCancelDialog from '@/views/Account/components/SubscriptionPauseCancelDialog.vue';
import SubscriptionResumeDialog from '@/views/Account/components/SubscriptionResumeDialog.vue';
import CustomilyPlanCard from '@/views/Account/components/CustomilyPlanCard.vue';
import StoreSelector from '@/components/StoreSelector/StoreSelector.vue';
import ShopifyBilling from '@/views/Account/components/ShopifyBilling.vue';
import { StoreType } from '@/typings/user';

const emit = defineEmits(['user-updated']);

const invoicesPerPage = 10;
const activeStatuses: SubscriptionStatus[] = ['active', 'trialing', 'past_due'];
const invoiceTableFields = [
    { key: 'date', label: 'Date' },
    { key: 'description', label: 'Description' },
    { key: 'amount', label: 'Amount', class: 'text-center' },
    { key: 'status', label: 'Status' },
    { key: 'invoice', label: 'Invoice', class: 'text-center' }
];

const storeUrl = ref('');
const isShopifyNewPlan = ref(false);
const paymentMethod: Ref<PaymentMethod | null> = ref(null);
const subscription: Ref<Subscription | null> = ref(null);
const invoices: Ref<Invoice[]> = ref([]);
const customer: Ref<Customer | null> = ref(null);
const pauseCardToken: Ref<string | undefined> = ref(undefined);

const loadingPaymentMethod = ref(true);
const loadingInvoices = ref(true);
const loadingSubscriptions = ref(true);
const showUpdateCardDialog = ref(false);
const showSubscriptionPauseCancelDialog = ref(false);
const showSubscriptionResumeDialog = ref(false);
const showCardAskDialog = ref(false);

const disablePrevBtn = ref(false);
const disableNextBtn = ref(false);

const cancelPauseDialogAllowCancel = ref(true);

const user = computed(() => store.state.user);

const subscriptions = computed(() => store.state.subscriptions);

const stripePublicKey = computed(() => store.state.stripePublicKey);

const subscriptionPausedOrWillPause = computed(
    () => subscription.value?.Status === 'paused' || subscription.value?.WillPauseAt
);

const invoiceItems = computed(() => {
    return invoices.value.map((i) => ({
        date: formatDateString(new Date(i.Created)),
        amount: `$${(i.Total / 100).toFixed(2)}`,
        status: i.Status,
        invoice: i.InvoicePdf
    }));
});

const hasActiveSubscription = computed(() => {
    if (!subscription.value) return false;
    return activeStatuses.includes(subscription.value.Status);
});

const accountDisableMode = computed(() => {
    if (!subscription.value) return false;

    // Check if the current subscription is fully active, that is, it's currently active and it won't be disabled in
    //  the future at the end of the billing cycle
    const isSubscriptionActive =
        hasActiveSubscription.value && !subscription.value.WillCancelAt && !subscription.value.WillPauseAt;

    const activeSubscriptions = subscriptions.value.filter(
        (s) => activeStatuses.includes(s.Status) && !s.WillCancelAt && !s.WillPauseAt
    );

    // If the current subscription is active and there's only 1 subscription active, then this is the only one,
    //  tell the user pausing/canceling this last store will suspend the entire Customily user.
    return (activeSubscriptions.length === 1 && isSubscriptionActive) || !activeSubscriptions.length;
});

const actionEntityString = computed(() => (accountDisableMode.value ? 'account' : 'store'));

const actionEntityStringCapitalized = computed(
    () => actionEntityString.value.charAt(0).toUpperCase() + actionEntityString.value.slice(1)
);

const userStores = computed(() => user.value?.Stores || []);

watch(storeUrl, () => {
    // If there are no stores matching this URL, select the first one available
    if (!user.value?.Stores.some((s) => (s.HasBilling || s.Type === StoreType.Shopify) && s.Url == storeUrl.value)) {
        const validStores = user.value?.Stores.filter((s) => s.HasBilling || s.Type === StoreType.Shopify);
        if (validStores?.length) {
            storeUrl.value = validStores[0].Url;
            isShopifyNewPlan.value = !validStores[0].HasBilling && validStores[0].Type === StoreType.Shopify;
        }
    }
    reloadData(false);
});

watch(showUpdateCardDialog, async () => {
    if (!showUpdateCardDialog.value) {
        loadingPaymentMethod.value = true;
        emit('user-updated');

        // Reload the subscription and the payment method for it
        await loadSubscriptions();
        await loadPaymentMethod();
    }
});

onBeforeMount(() => {
    if (user.value && user.value.Stores?.length) {
        // Get the initial store from the URL if available
        const url = new URL(location.href);
        const store = url.searchParams.get('store');
        if (store) {
            storeUrl.value = store;
            const currentStore = user.value?.Stores.find((s) => s.Url === store);
            isShopifyNewPlan.value = !currentStore?.HasBilling && currentStore?.Type === StoreType.Shopify;
        } else {
            const filteredStores = user.value.Stores.filter((s) => s.HasBilling || s.Type === StoreType.Shopify);
            storeUrl.value = filteredStores[0]?.Url || '';
            isShopifyNewPlan.value = !filteredStores[0].HasBilling && filteredStores[0].Type === StoreType.Shopify;
        }
        reloadData();
    }
});

const setStoreUrl = (newStoreUrl: string) => {
    if (storeUrl.value !== newStoreUrl) {
        storeUrl.value = newStoreUrl;
        const currentStore = user.value?.Stores.find((s) => s.Url === newStoreUrl);
        isShopifyNewPlan.value = !currentStore?.HasBilling && currentStore?.Type === StoreType.Shopify;
        // Reload all the data for the new store
        reloadData(false);
    }
};

const formatDateString = (date: Date | string): string => {
    return dateformat(date, 'mmm dd, yyyy');
};

const startSubscriptionCancelFlow = () => {
    cancelPauseDialogAllowCancel.value = true;
    showSubscriptionPauseCancelDialog.value = true;
};

const startSubscriptionPauseFlow = (askForCard = false) => {
    // Ask for a credit card before pausing the subscription
    if (askForCard) {
        pauseCardToken.value = undefined;
        showCardAskDialog.value = true;
    } else {
        cancelPauseDialogAllowCancel.value = false;
        showSubscriptionPauseCancelDialog.value = true;
    }
};

const startSubscriptionResumeFlow = () => {
    showSubscriptionResumeDialog.value = true;
};

const onCardForPauseSubmit = (token: string) => {
    // Now that we have the card token, start the subscription pausing
    showCardAskDialog.value = false;
    pauseCardToken.value = token;
    startSubscriptionPauseFlow();
};

const goPrevInvoicesPage = async () => {
    try {
        loadingInvoices.value = true;
        const data = await getInvoices(storeUrl.value, invoicesPerPage, undefined, invoices.value[0].Id);
        invoices.value = data.Invoices;

        disableNextBtn.value = disablePrevBtn.value = false;
        if (!data.MoreAvailable) {
            disablePrevBtn.value = true;
        }
    } finally {
        loadingInvoices.value = false;
    }
};

const goNextInvoicesPage = async () => {
    try {
        loadingInvoices.value = true;
        const data = await getInvoices(storeUrl.value, invoicesPerPage, invoices.value[invoices.value.length - 1].Id);
        invoices.value = data.Invoices;

        disableNextBtn.value = disablePrevBtn.value = false;
        if (!data.MoreAvailable) {
            disableNextBtn.value = true;
        }
    } finally {
        loadingInvoices.value = false;
    }
};

const editCreditCard = () => {
    showUpdateCardDialog.value = true;
};

const onCardSubmit = () => {
    showUpdateCardDialog.value = false;
};

const onSubscriptionCancel = async () => {
    emit('user-updated');
    pauseCardToken.value = undefined;
    loadingSubscriptions.value = true;
    await loadSubscriptions();
};

const onSubscriptionPaused = async () => {
    emit('user-updated');
    pauseCardToken.value = undefined;
    loadingSubscriptions.value = true;
    await loadSubscriptions();
};

const onSubscriptionResumed = async () => {
    emit('user-updated');
    pauseCardToken.value = undefined;
    loadingSubscriptions.value = true;
    await loadSubscriptions();
};

const loadSubscriptions = async (forceReload = true) => {
    loadingSubscriptions.value = true;
    if (forceReload) {
        const _subscriptions = await getSubscriptions();
        store.commit('subscriptions', _subscriptions);
    }
    subscription.value = subscriptions.value.find((s) => s.Store === storeUrl.value) || null;
    loadingSubscriptions.value = false;
};

const loadPaymentMethod = async () => {
    if (subscription.value && subscription.value.PaymentMethodId) {
        loadingPaymentMethod.value = true;
        paymentMethod.value = await getPaymentMethod(storeUrl.value);
    }
    loadingPaymentMethod.value = false;
};

const reloadData = async (forceReload = true) => {
    loadingPaymentMethod.value = true;
    loadingSubscriptions.value = true;
    loadingInvoices.value = true;

    getCustomer(storeUrl.value)
        .then((data) => {
            customer.value = data;
        })
        .catch((error) => {
            customer.value = null;
            if (error.request.status !== 404) {
                window.rumReportError(error);
            }
        });

    // Load subscriptions
    loadSubscriptions(forceReload)
        .then(() => {
            loadPaymentMethod().catch((error) => {
                if (error.request.status !== 404) {
                    window.rumReportError(error);
                }
                paymentMethod.value = null;
                loadingPaymentMethod.value = false;
            });
        })
        .catch((error) => {
            if (error.request.status !== 404) {
                window.rumReportError(error);
            }
            subscription.value = null;
            paymentMethod.value = null;
            loadingSubscriptions.value = false;
            loadingPaymentMethod.value = false;
        });

    try {
        const data = await getInvoices(storeUrl.value, invoicesPerPage);
        invoices.value = data.Invoices;

        disableNextBtn.value = false;
        disablePrevBtn.value = true;
        if (!data.MoreAvailable) {
            disableNextBtn.value = true;
        }
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.request?.status !== 404) {
            window.rumReportError(axiosError);
        }
        invoices.value = [];
    } finally {
        loadingInvoices.value = false;
    }
};
</script>

<style lang="scss">
.invoice-table {
    // https://stackoverflow.com/questions/7060009/css-max-height-remaining-space
    max-height: initial !important;

    td {
        padding: 0.3rem !important;
        vertical-align: middle !important;
    }

    th {
        padding: 0.3rem !important;
    }

    tr th:first-child,
    tr td:first-child {
        padding-left: 0 !important;
    }
}
</style>

<style lang="scss" scoped>
li {
    list-style-type: none;
    padding-left: 0;
}

.invoice-card {
    min-height: 400px;
}

.small-card-content.has-card {
    min-height: 70px;
}

.paid {
    font-weight: bold;
}

.card-expiration {
    color: gray;
}

.invoice-icon {
    color: gray;
    font-size: 18px;
}
</style>
